import { useState } from "react";
import { Drawer, IconButton, Popover, Box } from "@mui/material";
import { ButtonsContainer, Notification } from "../utilities";
import { useDispatch, useSelector } from "react-redux";
import Searchbar from "./Searchbar";
import { useLocation, Link } from "react-router-dom";
import { useQuery } from "react-query";
import {
  MenuIcon,
  ShipmentIcon,
  PickupsIcon,
  ExportDataIcon,
  AdvancedSearch,
  NewShipment,
  complaint,
  DataAnalysis,
  TicketsIcon,
} from "../../assets/icons";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../helpers/AxiosInstance";
import Translation from "../utilities/Translation";
import { handleLogout } from "../../redux/reducers/authReducer";
import logoImg from "../../assets/images/logo.png";
import styles from "../../assets/css/modules/mobileLayout.module.css";
import LogoutModal from "../modals/LogoutModal";

// Premium Icons
const MenuIconSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

const LogoutIconSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
    <polyline points="16 17 21 12 16 7"></polyline>
    <line x1="21" y1="12" x2="9" y2="12"></line>
  </svg>
);

const BellIconSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
  </svg>
);

const ChevronDown = ({ isOpen, active }) => (
  <svg
    className={`${styles.chevronIcon} ${active ? styles.chevronIconActive : ""}`}
    style={{
      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
    }}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SubMenuDot = ({ active }) => (
  <span
    className={styles.subMenuDot}
    style={{
      backgroundColor: active ? "var(--primary-color)" : "#cbd5e1",
    }}
  />
);

function MobileLayout({ RoleID }) {
  const { pathname } = useLocation();
  const instance = axiosInstance();
  
  const { data, refetch } = useQuery(
    "notifications",
    async () => {
      const { data } = await instance.get("/GetActiveNotifications");
      return data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 1000,
      cacheTime: 3000,
    }
  );

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("layout");
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const openNotification = Boolean(anchor);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const handleNotificationClick = (event) => {
    setAnchor(event.currentTarget);
  };

  const { Logo } = useSelector((state) => ({
    Logo:
      state.Auth.companyData.Logo ||
      `https://ahmedaglan.github.io/ClientsJSON/ClientImages/${
        state.Auth.companyData.ClientID || "999999"
      }/logo.png`,
  }));

  const { ClientName, DataAnalysisLink } = useSelector((state) => ({
    ClientName: state.Auth.AuthedUser.ClientName,
    DataAnalysisLink: state.Auth.AuthedUser.DataAnalysisLink,
  }));

  const isRTL = i18n.dir() === "rtl";

  const handleLogoutClick = () => {
    setOpen(false);
    setLogoutModalOpen(true);
  };

  const renderIcon = (iconPath, active, hoverKey) => {
    const isHovered = hoveredItem === hoverKey;
    const isActive = active || isHovered;

    // Custom SVG icons with new design
    const icons = {
      MenuIcon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="9" rx="1" />
          <rect x="14" y="3" width="7" height="5" rx="1" />
          <rect x="14" y="12" width="7" height="9" rx="1" />
          <rect x="3" y="16" width="7" height="5" rx="1" />
        </svg>
      ),
      ShipmentIcon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
          <polygon points="12 22.08 12 12 3 6.92 3 17.08 12 22.08"></polygon>
          <polygon points="12 12 21 6.92 21 17.08 12 22.08"></polygon>
          <polygon points="12 2 3 6.92 12 12 21 6.92 12 2"></polygon>
          <line x1="12" y1="22.08" x2="12" y2="12"></line>
        </svg>
      ),
      PickupsIcon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="1" x2="12" y2="23"></line>
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
        </svg>
      ),
      ExportDataIcon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      ),
      TicketsIcon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      ),
      AdvancedSearch: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      ),
      NewShipment: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="3" width="15" height="13"></rect>
          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
          <circle cx="5.5" cy="18.5" r="2.5"></circle>
          <circle cx="18.5" cy="18.5" r="2.5"></circle>
        </svg>
      ),
      complaint: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      ),
      DataAnalysis: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      ),
    };

    // Get icon based on the iconPath
    const iconKey = Object.keys(icons).find(key => iconPath.includes(key));
    const IconComponent = iconKey ? icons[iconKey] : null;

    if (IconComponent) {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            flexShrink: 0,
            transition: "all 0.2s ease",
            transform: isActive ? "scale(1.05)" : "scale(1)",
          }}
        >
          {IconComponent}
        </span>
      );
    }

    // Fallback to original method
    return (
      <span
        style={{
          display: "inline-block",
          width: 22,
          height: 22,
          flexShrink: 0,
          backgroundColor:
            active || isHovered ? "var(--primary-color)" : "#64748b",
          maskImage: `url(${iconPath})`,
          WebkitMaskImage: `url(${iconPath})`,
          maskSize: "contain",
          WebkitMaskSize: "contain",
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
          maskPosition: "center",
          WebkitMaskPosition: "center",
          transition: "background-color 0.2s ease",
        }}
      />
    );
  };

  const toggleMenu = (key) => {
    setExpandedMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path) => {
    if (path === "/") return pathname === "/";
    return pathname.startsWith(path);
  };

  const isGroupActive = (paths) => {
    return paths.some((p) => pathname.startsWith(p));
  };

  const menuItems = [
    {
      type: "single",
      to: "/",
      icon: MenuIcon,
      label: t("Sidebar.Menu"),
      key: "dashboard",
    },
    {
      type: "group",
      icon: ShipmentIcon,
      label: t("Sidebar.Orders"),
      key: "orders",
      children: [
        { to: "/reports/shipments", label: t("Sidebar.List") },
        { to: "/new-shipment", label: t("Navbar.add_shipment") },
        { to: "/upload", label: t("Navbar.upload_excel") },
      ],
    },
    {
      type: "single",
      to: "/cs-home",
      icon: complaint,
      label: t("Sidebar.Rejected_Orders"),
      key: "rejected",
    },
    {
      type: "group",
      icon: PickupsIcon,
      label: t("Sidebar.Revenue"),
      key: "revenue",
      children: [{ to: "/reports/pickups", label: t("Sidebar.List") }],
    },
    {
      type: "single",
      to: "/new-pickup",
      icon: NewShipment,
      label: t("Sidebar.Pickup_Request"),
      key: "pickup-request",
    },
  ];

  const extraItems = [
    {
      to: "/reports/export",
      icon: ExportDataIcon,
      label: t("Sidebar.Reports_ExportData"),
      key: "export",
    },
    {
      to: "/multi-search",
      icon: AdvancedSearch,
      label: "Print AWB",
      key: "search",
    },
    {
      to: "/tickets",
      icon: TicketsIcon,
      label: t("Sidebar.Tickets"),
      key: "tickets",
    },
  ];

  if (DataAnalysisLink && DataAnalysisLink.trim() !== "") {
    extraItems.push({
      to: "/data-analysis",
      icon: DataAnalysis,
      label: t("Sidebar.data_analysis"),
      key: "analysis",
    });
  }

  return (
    <div style={{ position: "sticky", top: 0, maxHeight: "100vh", zIndex: 243 }}>
      {/* Premium Glass Header Bar */}
      <div className={styles.headerBar}>
        
        {/* Left Side: Hamburger trigger */}
        {RoleID === 2 && (
          <button
            className={`${styles.iconBtn} ${open ? styles.iconBtnActive : ""}`}
            onClick={() => setOpen(!open)}
            type="button"
          >
            <MenuIconSVG />
          </button>
        )}

        {/* Center / Controls: Notification, Searchbar, Logout, Translation */}
        <div className={styles.controlsBar}>
          
          {RoleID === 1 && (
            <button
              className={styles.iconBtn}
              onClick={handleLogoutClick}
              type="button"
            >
              <LogoutIconSVG />
            </button>
          )}

          {RoleID === 2 && (
            <IconButton
              sx={{
                background: "transparent",
                position: "relative",
                color: "#ffffff",
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                "&:hover": { background: "rgba(255, 255, 255, 0.12)", color: "#ffffff" },
              }}
              onClick={handleNotificationClick}
            >
              <BellIconSVG />
              {data?.notifications?.length > 0 && (
                <span className={styles.notificationBadge} />
              )}
            </IconButton>
          )}

          <Translation isMobile />
          {!pathname.includes("cshome") && <Searchbar />}
        </div>

        {/* Right Side: Logo */}
        <div style={{ display: "grid", placeItems: "center", width: "42px", height: "42px" }}>
          <img
            src={Logo}
            alt="Logo"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </div>
      </div>

      {/* Side Navigation Drawer */}
      <Drawer
        anchor={isRTL ? "right" : "left"}
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
            border: "none",
          },
        }}
      >
        <div dir={isRTL ? "rtl" : "ltr"} className={styles.sidebarContainer}>
          {/* User Profile Info section */}
          <div className={styles.userSection}>
            <div className={styles.userProfile}>
              <div className={styles.avatarBox}>
                <img
                  src={logoImg}
                  alt="Logo"
                  className={styles.avatarImg}
                />
              </div>
              <span className={styles.userName}>{ClientName || "User"}</span>
            </div>
          </div>

          {/* Navigation link stacks */}
          <nav className={styles.navSection}>
            {menuItems.map((item) => {
              if (item.type === "single") {
                const active = isActive(item.to);
                return (
                  <Link
                    key={item.key}
                    to={item.to}
                    className={`${styles.navItem} ${active ? styles.navItemActive : ""}`}
                    onMouseEnter={() => setHoveredItem(item.key)}
                    onMouseLeave={() => setHoveredItem(null)}
                    onClick={() => setOpen(false)}
                  >
                    {renderIcon(item.icon, active, item.key)}
                    <span>{item.label}</span>
                  </Link>
                );
              }

              // Group items
              const groupActive = isGroupActive(item.children.map((c) => c.to));
              const isExpanded = expandedMenus[item.key] || groupActive;

              return (
                <div key={item.key}>
                  <div
                    className={`${styles.navItem} ${groupActive ? styles.navItemActive : ""}`}
                    onClick={() => toggleMenu(item.key)}
                    onMouseEnter={() => setHoveredItem(item.key)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    {renderIcon(item.icon, groupActive, item.key)}
                    <span style={{ flex: 1 }}>{item.label}</span>
                    <ChevronDown
                      isOpen={isExpanded}
                      active={groupActive}
                    />
                  </div>
                  
                  <div
                    className={styles.subMenu}
                    style={{
                      maxHeight: isExpanded ? `${item.children.length * 44}px` : "0px",
                      opacity: isExpanded ? 1 : 0,
                    }}
                  >
                    {item.children.map((child) => {
                      const childActive = isActive(child.to);
                      return (
                        <Link
                          key={child.to}
                          to={child.to}
                          className={`${styles.subNavItem} ${childActive ? styles.subNavItemActive : ""}`}
                          onMouseEnter={() => setHoveredItem(`sub-${child.to}`)}
                          onMouseLeave={() => setHoveredItem(null)}
                          onClick={() => setOpen(false)}
                        >
                          <SubMenuDot active={childActive} />
                          <span>{child.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Extra items */}
            {extraItems.map((item) => {
              const active = isActive(item.to);
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  className={`${styles.navItem} ${active ? styles.navItemActive : ""}`}
                  onMouseEnter={() => setHoveredItem(item.key)}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={() => setOpen(false)}
                >
                  {renderIcon(item.icon, active, item.key)}
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </Drawer>

      {/* Notifications Popover */}
      <Popover
        open={openNotification}
        anchorEl={anchor}
        onClick={() => {
          refetch();
          setAnchor(null);
        }}
        onClose={() => setAnchor(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: isRTL ? "left" : "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: isRTL ? "left" : "right",
        }}
      >
        <div dir="rtl" className={styles.popoverCard}>
          {data
            ? data.notifications
                .slice(0, 3)
                .map((notification, index) => (
                  <Notification
                    key={index}
                    AWB={notification.AWB}
                    Body={notification.Body}
                    Status={notification.StatusName}
                    Serial={notification.Serial}
                    ConsigneeName={notification.ConsigneeName}
                    ConsigneePhone={notification.ConsigneePhone}
                    RunnerName={notification.RunnerName}
                    RunnerPhone={notification.RunnerPhone}
                    ReasonName={notification.ReasonName}
                    COD={notification.COD}
                  />
                ))
            : null}
          <hr className={styles.dividerLine} />
          <ButtonsContainer style={{ marginTop: "10px", marginBottom: "10px" }}>
            <Link to="/notifications" onClick={() => setAnchor(null)} className={styles.popoverBtn}>
              {t("Navbar.all_notifications")}
            </Link>
          </ButtonsContainer>
        </div>
      </Popover>

      {/* Logout Confirmation Modal */}
      <LogoutModal
        isOpen={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
      />
    </div>
  );
}

export default MobileLayout;
